import { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import { useMount, useUnmount } from "./hooks";

/**
 * Accessor to the storage.
 *
 * please also modify GlobalState in packages/core-global-state/src/globalState.ts
 * when you modify this interface. Scenario: https://microsoft.visualstudio.com/OS/_workitems/edit/41542542
 */
export interface GlobalState<T> {
    /**
     * Set new value of the storage.
     * @param value New value
     */
    setValue(value: T): void;

    /**
     * Get the value from the storage and component will get notify when value
     * change.
     */
    useValue(): T;
}

/**
 * This is a nice type for subcriber functions.
 *
 * please also modify GlobalStateSubscriber in packages/core-global-state/src/globalState.ts
 * when you modify this interface. Scenario: https://microsoft.visualstudio.com/OS/_workitems/edit/41542542
 */
type GlobalStateSubscriber<T> = (newValue: T) => void;

/**
 * This function will be used to update useState data.
 * This is mainly use to signal component re-rendering;
 *
 * please also modify useUpdateValue in packages/core-global-state/src/globalState.ts
 * when you modify this function. Scenario: https://microsoft.visualstudio.com/OS/_workitems/edit/41542542
 *
 * @param initialValue The initial value
 */
const useUpdateValue = <T>(initialValue: T) => {
    // Create a proxy state so that component will get notified about changes.
    const [, componentSetValue] = useState(initialValue);

    return useCallback((newValue: T) => {
        componentSetValue(newValue);
    }, []);

};

/**
 * Create a storage that component can access to the data and get re-rendered
 * when data change.
 *
 * Note: you will need to useMount for the top component when consume this
 * function otherwise the value will get reset everytime the component
 * re-render.
 *
 * The reason we dont do useMount within the implementation is so that global
 * state can get init outside the scope of a component.
 *
 * please also modify createGlobalState in packages/core-global-state/src/globalState.ts
 * when you modify this function. Scenario: https://microsoft.visualstudio.com/OS/_workitems/edit/41542542
 *
 * @param initialValue The initial value in the storage.
 * @returns This will return accessor to value and setter of the storage.
 */
export function createGlobalState<T>(initialValue: T): GlobalState<T> {

    // "storedValue" is the actual value that will be used by all components
    let storedValue: T = initialValue;

    // This is the list of all sub
    let setStateFunctions: readonly GlobalStateSubscriber<T>[] = [];

    // We create a new "setValue" function that will change "storedValue"
    // Component will have access to this function to try the value in the storage.
    function setValue(newValue: T): void {

        // don't needlessly rebroadcast if nothing changed
        if (storedValue !== newValue) {
            storedValue = newValue;

            // Then annouce the change to all subscribers.
            // "unstable_batchedUpdates" is recommended by Dan Abramov of the React team when making state changes
            // outside a React event handler. https://stackoverflow.com/a/48610973/1287183
            ReactDOM.unstable_batchedUpdates(() => {
                setStateFunctions.forEach(fn => fn(newValue));
            });
        }
    }

    /**
     * Component will call this function to get access to value and subscribe to
     * change.
     */
    function useValue(): T {
        // Whenever a component use this hook. It will call useState hook within
        // useUpdateValue so that component will get notify when changes happen.

        // We use store value to init the proxy state with the latest value
        // within the storage. This means everytime a component call useValue,
        // the proxy state will use the latest value as init value.
        const updateValue = useUpdateValue(storedValue);

        useMount(() => {
            // Then store the function to notify change in a list.
            setStateFunctions = setStateFunctions.concat(updateValue);
        });

        useUnmount(() => {
            // And remove itself when the component goes away.
            setStateFunctions = setStateFunctions.filter(value => value !== updateValue);
        });

        // When component get re-render by "componentSetValue" signal, component
        // will pick up the value in storedValue here, as useValue will get
        // re-run.
        return storedValue;
    }

    return {
        setValue,
        useValue
    };
}
